import { useState, useRef } from "react";
import { Upload, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

export default function UploadPage() {
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setCreated(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/upload-document", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = r.data.job_id;
      for (let i = 0; i < 60; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const j = await api.get(`/upload-jobs/${jobId}`);
        if (j.data.status === "done") {
          setCreated(j.data.created);
          toast.success(`Found ${j.data.created.length} item${j.data.created.length === 1 ? "" : "s"}! Added to the calendar.`);
          setBusy(false);
          return;
        }
        if (j.data.status === "error") {
          toast.error(j.data.error || "Couldn't read that document.");
          setBusy(false);
          return;
        }
      }
      toast.error("That took too long — try again with a smaller file.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't read that document. Try a clearer file.");
    }
    setBusy(false);
  };

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold">Upload schedule</h1>
        <p className="text-gray-600 text-sm mt-1">Drop in your assessment schedule or assignment sheet (PDF or photo). Mia will read it and fill the calendar for you.</p>
      </div>

      <button
        onClick={() => fileRef.current?.click()}
        disabled={busy}
        data-testid="upload-dropzone"
        className="w-full border-4 border-dashed border-black rounded-2xl bg-white p-10 flex flex-col items-center gap-3 hover:bg-[#FFE066]/20 transition-colors disabled:opacity-60"
      >
        {busy ? (
          <>
            <Loader2 size={40} className="animate-spin" />
            <p className="font-bold">Mia's reading it...</p>
            <p className="text-sm text-gray-500">This takes ~15 seconds</p>
          </>
        ) : (
          <>
            <Upload size={40} strokeWidth={2.5} />
            <p className="font-bold">Tap to choose a file</p>
            <p className="text-sm text-gray-500">PDF, JPG, PNG or TXT</p>
          </>
        )}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv"
        className="hidden"
        data-testid="upload-file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {created && (
        <div className="space-y-3" data-testid="upload-results">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
            <CheckCircle2 size={20} className="text-[#51CF66]" /> Added to calendar
          </h2>
          {created.map((a) => (
            <div key={a.id} className="flex items-center gap-3 p-4 bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_rgba(0,0,0,1)]">
              <FileText size={20} className="shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-sm">{a.title}</p>
                <p className="text-xs text-gray-600">{a.subject} · {a.type} · due {a.due_date.slice(0, 10)}</p>
              </div>
            </div>
          ))}
          <Link to="/calendar" data-testid="view-calendar-link" className="bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold px-6 py-3 flex items-center justify-center w-full">
            View calendar
          </Link>
        </div>
      )}
    </div>
  );
}
